// src/services/eventUpgradeInvoiceService.js
// Service for creating and managing event tier upgrade invoices
// Invoice format: EVT-YY-NNNNNNNNN (e.g., EVT-25-000000001)

import supabase from './supabase';

// Platform details (Invits / Forte Technologies)
export const PLATFORM_DETAILS = {
  name: 'Invits',
  tradeName: 'Forte Technologies',
  gstin: '22MIYPS5170D1ZN',
  pan: 'MIYPS5170D',
  address: 'Raipur, Chhattisgarh - 492001',
  state: 'Chhattisgarh',
  stateCode: '22',
  email: 'support@invits.co',
  hsnSac: '998314', // Online content services
};

// State codes for GST (first 2 digits of GSTIN)
export const STATE_CODES = {
  'Andhra Pradesh': '37',
  'Arunachal Pradesh': '12',
  'Assam': '18',
  'Bihar': '10',
  'Chhattisgarh': '22',
  'Goa': '30',
  'Gujarat': '24',
  'Haryana': '06',
  'Himachal Pradesh': '02',
  'Jharkhand': '20',
  'Karnataka': '29',
  'Kerala': '32',
  'Madhya Pradesh': '23',
  'Maharashtra': '27',
  'Manipur': '14',
  'Meghalaya': '17',
  'Mizoram': '15',
  'Nagaland': '13',
  'Odisha': '21',
  'Punjab': '03',
  'Rajasthan': '08',
  'Sikkim': '11',
  'Tamil Nadu': '33',
  'Telangana': '36',
  'Tripura': '16',
  'Uttar Pradesh': '09',
  'Uttarakhand': '05',
  'West Bengal': '19',
  'Andaman and Nicobar Islands': '35',
  'Chandigarh': '04',
  'Dadra and Nagar Haveli and Daman and Diu': '26',
  'Delhi': '07',
  'Jammu and Kashmir': '01',
  'Ladakh': '38',
  'Lakshadweep': '31',
  'Puducherry': '34',
};

/**
 * Get state code from state name
 */
export const getStateCode = (stateName) => {
  if (!stateName) return '';
  return STATE_CODES[stateName] || '';
};

/**
 * Get all Indian states for dropdown
 */
export const getIndianStates = () => {
  return Object.keys(STATE_CODES).sort();
};

/**
 * Get current financial year from server
 * Returns 2-digit year (e.g., "25" for FY 2025-26)
 */
export const getFinancialYear = async () => {
  try {
    const { data, error } = await supabase.rpc('get_event_tier_financial_year');
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting financial year from server:', error);
    // Fallback to local calculation
    const now = new Date();
    const month = now.getMonth(); // 0-11
    const year = now.getFullYear();
    // Financial year: April to March
    // If month is Jan(0), Feb(1), Mar(2), FY started previous year
    const fyYear = month < 3 ? year - 1 : year;
    return fyYear.toString().slice(-2);
  }
};

/**
 * Calculate GST breakdown based on customer state
 * Platform state: Chhattisgarh
 * Same state = CGST + SGST (intrastate)
 * Different state = IGST (interstate)
 */
export const calculateEventTierGST = (baseAmount, customerState) => {
  const gstRate = 18;
  const gstAmount = (baseAmount * gstRate) / 100;
  const platformState = PLATFORM_DETAILS.state;

  const isSameState = customerState &&
    customerState.toLowerCase().trim() === platformState.toLowerCase().trim();

  if (isSameState) {
    const halfGst = gstAmount / 2;
    return {
      type: 'intrastate',
      cgst: halfGst,
      sgst: halfGst,
      igst: 0,
      totalGst: gstAmount,
      rate: gstRate,
    };
  } else {
    return {
      type: 'interstate',
      cgst: 0,
      sgst: 0,
      igst: gstAmount,
      totalGst: gstAmount,
      rate: gstRate,
    };
  }
};

/**
 * Get invoice by ID
 */
export const getEventTierInvoiceById = async (invoiceId) => {
  try {
    const { data, error } = await supabase
      .from('event_tier_invoices')
      .select('*')
      .eq('id', invoiceId)
      .single();

    if (error) throw error;

    return { success: true, invoice: data };
  } catch (error) {
    console.error('Error fetching event tier invoice:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get event tier invoice by order ID
 */
export const getEventTierInvoiceByOrderId = async (orderId) => {
  try {
    const { data, error } = await supabase
      .from('event_tier_invoices')
      .select('*')
      .eq('order_id', orderId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: false, notFound: true };
      }
      throw error;
    }

    return { success: true, invoice: data };
  } catch (error) {
    console.error('Error fetching event tier invoice by order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all event tier invoices for current user
 * @param {Object} options - Query options
 */
export const getUserEventTierInvoices = async (options = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      .from('event_tier_invoices')
      .select('*')
      .eq('user_id', user.id)
      .order('generated_at', { ascending: false });

    if (options.financialYear) {
      query = query.eq('financial_year', options.financialYear);
    }

    if (options.tierType) {
      query = query.eq('tier_type', options.tierType);
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, invoices: data || [] };
  } catch (error) {
    console.error('Error fetching user event tier invoices:', error);
    return { success: false, error: error.message, invoices: [] };
  }
};

/**
 * Get invoice statistics for current user
 */
export const getUserInvoiceStats = async (financialYear = null) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      .from('event_tier_invoices')
      .select('total_amount, total_gst, tier_type')
      .eq('user_id', user.id);

    if (financialYear) {
      query = query.eq('financial_year', financialYear);
    }

    const { data, error } = await query;

    if (error) throw error;

    const stats = {
      totalInvoices: data.length,
      totalSpent: data.reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0),
      totalGstPaid: data.reduce((sum, inv) => sum + parseFloat(inv.total_gst), 0),
      byType: {
        host: data.filter(inv => inv.tier_type === 'host').length,
        photo: data.filter(inv => inv.tier_type === 'photo').length,
      },
    };

    return { success: true, stats };
  } catch (error) {
    console.error('Error fetching invoice stats:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get tier type display label
 */
export const getTierTypeLabel = (tierType) => {
  switch (tierType) {
    case 'host':
      return 'Host Features Upgrade';
    case 'photo':
      return 'Photo Upload Upgrade';
    default:
      return 'Event Upgrade';
  }
};

/**
 * Get tier display label
 */
export const getTierDisplayLabel = (tierName, tierDisplayName) => {
  if (tierDisplayName) return tierDisplayName;

  const labels = {
    'free': 'Free Plan',
    'basic': 'Basic Plan',
    'pro': 'Pro Plan',
    'elite': 'Elite Plan',
  };
  return labels[tierName] || tierName;
};

/**
 * Format currency
 */
export const formatCurrency = (amount) => {
  return `₹${parseFloat(amount).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export default {
  getFinancialYear,
  calculateEventTierGST,
  getEventTierInvoiceById,
  getEventTierInvoiceByOrderId,
  getUserEventTierInvoices,
  getUserInvoiceStats,
  getTierTypeLabel,
  getTierDisplayLabel,
  getStateCode,
  getIndianStates,
  formatCurrency,
  PLATFORM_DETAILS,
  STATE_CODES,
};
